import { Box, Grid, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import {
  FormDatePicker,
  FormTextField,
  GridCardContent,
} from "components/atoms";
import { AppTable, ReportHeader } from "components/molecules";
import { FormikHelpers, useFormik } from "formik";
import { TableHelper, TimeHelper } from "helpers";
import { usePaginationRequest } from "hooks/useRequest";
import {
  IGetSlowMovingStockRequest,
  IPaginationRequest,
  ISlowMoving,
} from "model";
import moment from "moment";
import { useState } from "react";
import { CycleCountApiService, ReportApiService } from "services";
import { I18nTranslator, translations } from "utils/i18n";
import { CURRENT_PAGE_SIZE, FIRST_PAGE } from "utils/Const";

const SlowMovingStockReport = () => {
  const [request, setRequest] = useState<IPaginationRequest>({
    fromDate: TimeHelper.startOfWeek("MM-DD-YYYY"),
    toDate: TimeHelper.endOfWeek("MM-DD-YYYY"),
    pageNumber: FIRST_PAGE,
    pageSize: CURRENT_PAGE_SIZE,
  });

  const handleSubmit = (values: IGetSlowMovingStockRequest) => {
    setRequest({
      ...request,
      ...values,
    });
  };

  const handleChangePage = (pageNumber: number) => {
    setRequest({
      ...request,
      pageNumber: pageNumber,
    });
  };

  const handlePageSizeChange = (pageSize: number) => {
    setRequest({ ...request, pageSize: pageSize });
  };
  return (
    <>
      <DetailHeader request={request} onSubmit={handleSubmit} />
      <TableSection
        request={request}
        onChangePage={handleChangePage}
        onPageSizeChange={handlePageSizeChange}
      />
    </>
  );
};

const DetailHeader = (props: {
  request: IPaginationRequest;
  onSubmit(values: IPaginationRequest): void;
}) => {
  const { request, onSubmit } = props;
  const formik = useFormik<any>({
    initialValues: {
      productName: "",
      fromDate: moment(request.fromDate, "MM-DD-YYYY"),
      toDate: moment(request.toDate, "MM-DD-YYYY"),
    },
    onSubmit: (values: any, { validateForm }: FormikHelpers<any>) => {
      const request: IPaginationRequest = {
        pageNumber: 1,
        fromDate: TimeHelper.formatMomentToDate(values.fromDate, "MM-DD-YYYY"),
        toDate: TimeHelper.formatMomentToDate(values.toDate, "MM-DD-YYYY"),
      };
      onSubmit(request);
    },
  });
  return (
    <ReportHeader
      title={I18nTranslator(translations.routes.cycleCount)}
      formik={formik}
      loading={false}
    >
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={3} item>
          <FormDatePicker
            id="fromDate"
            label={I18nTranslator(translations.fromDate)}
          />
        </Grid>
        <Grid xs={3} item>
          <FormDatePicker
            id="toDate"
            label={I18nTranslator(translations.toDate)}
          />
        </Grid>
      </Grid>
    </ReportHeader>
  );
};

const TableSection = (props: {
  request: IPaginationRequest;
  onChangePage(pageNumber: number): void;
  onPageSizeChange(pageSize: number): void;
}) => {
  const { request, onChangePage, onPageSizeChange } = props;
  const { loading, result } = usePaginationRequest<IPaginationRequest, any[]>(
    request,
    CycleCountApiService.getAllCycleCounts
  );

  const COLUMNS: GridColDef[] = [
    TableHelper.renderSTT(),
    TableHelper.createCell({
      field: "palletCode",
      headerName: I18nTranslator(translations.report.palletCode),
      size: "small",
      align: "left",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "locationCode",
      headerName: I18nTranslator(translations.report.locationCode),
      size: "small",
      align: "left",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "locationPutDate",
      headerName: I18nTranslator(translations.report.locationPutDate),
      size: "small",
      align: "left",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "productCode",
      headerName: I18nTranslator(translations.report.productCode),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "productName",
      headerName: I18nTranslator(translations.report.productName),
      size: "medium",
      align: "left",
    }),
    TableHelper.createCell({
      field: "productLot",
      headerName: I18nTranslator(translations.report.productLot),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "batchCodeDistributor",
      headerName: I18nTranslator(translations.report.batchCodeDistributor),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "uom",
      headerName: I18nTranslator(translations.report.uom),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "manufacturingDate",
      headerName: I18nTranslator(translations.report.manufacturingDate),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "expiryDate",
      headerName: I18nTranslator(translations.report.expiryDate),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "quantity",
      headerName: I18nTranslator(translations.report.inventoryQuantity),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "monthLeft",
      headerName: I18nTranslator(translations.report.timeLeft),
      size: "small",
      align: "right",
      renderHeader: () => {
        return (
          <Box sx={{}}>
            <Typography fontWeight={"600"}>
              {I18nTranslator(translations.report.timeLeft)}
            </Typography>
            <Typography fontWeight={"600"}>
              {I18nTranslator(translations.report.month)}
            </Typography>
          </Box>
        );
      },
      renderCell: ({ value }) => <Typography>{`-${value}`}</Typography>,
    }),
    TableHelper.createCell({
      field: "yearLeft",
      headerName: I18nTranslator(translations.report.timeLeft),
      size: "small",
      align: "right",
      renderHeader: () => {
        return (
          <Box sx={{}}>
            <Typography fontWeight={"600"}>
              {I18nTranslator(translations.report.timeLeft)}
            </Typography>
            <Typography fontWeight={"600"}>
              {I18nTranslator(translations.report.month)}
            </Typography>
          </Box>
        );
      },
      renderCell: ({ value }) => <Typography>{`-${value}`}</Typography>,
    }),
  ];

  return (
    <GridCardContent sx={{ flexDirection: "column" }}>
      <Box sx={{ width: "100%" }}>
        <AppTable<any>
          loading={loading}
          data={result}
          columns={COLUMNS}
          onSearch={(searchValue: string) => {}}
          searchPlaceholder={I18nTranslator(
            translations.placeholder.searchReceiveDocument
          )}
          onChangePage={onChangePage}
          hideHeader
          removeActionColumn
          defaultHeaderTable={170}
          onPageSizeChange={onPageSizeChange}
        />
      </Box>
    </GridCardContent>
  );
};

export default SlowMovingStockReport;
